import { useState } from "react";
import { ChevronRight, Paperclip, Eye, Download, Lock, PlayCircle, CheckCircle2, HelpCircle, CalendarClock, UserRound } from "lucide-react";
import { dateBR, dateTimeBR } from "../lib/format";
import { Button } from "./ui/button";

export const Block = ({ title, children, testid }) => <section className="rounded-xl border bg-white p-4 space-y-3" data-testid={testid}><div className="label-xs">{title}</div>{children}</section>;

export function Collapse({ icon: Icon, title, children, testid }) {
  const [o, setO] = useState(false);
  return <div className="border-t pt-3"><button type="button" onClick={() => setO(!o)} data-testid={testid} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"><ChevronRight className={`h-4 w-4 transition-transform ${o ? "rotate-90" : ""}`} /><Icon className="h-4 w-4" />{title}</button>{o && <div className="mt-3 space-y-3">{children}</div>}</div>;
}

export function FileLine({ name, sub, onView, onDownload, testid, internal, pending }) {
  return (
    <div className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2" data-testid={testid}>
      <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
      <span className="min-w-0 flex-1"><span className="block truncate font-medium">{name}{internal && <Lock className="inline h-3 w-3 ml-1 text-violet-600" />}{pending && <span className="ml-1 text-[10px] font-semibold text-sky-700">aguardando envio</span>}</span>{sub && <span className="block text-[11px] text-slate-500">{sub}</span>}</span>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onView} data-testid={`${testid}-view`}><Eye className="h-3.5 w-3.5 mr-1" />Visualizar</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onDownload} data-testid={`${testid}-download`}><Download className="h-3.5 w-3.5 mr-1" />Baixar</Button>
    </div>);
}

export const STATUS = {
  nova: ["NOVA", "bg-rose-50 text-rose-700 border-rose-200"], processando: ["EM PROCESSAMENTO", "bg-sky-50 text-sky-800 border-sky-300"],
  aguardando: ["AGUARDANDO CLIENTE", "bg-amber-50 text-amber-800 border-amber-300"], concluida: ["CONCLUÍDA", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  encerrada: ["ENCERRADA", "bg-slate-100 text-slate-600 border-slate-200"],
};

export function TreatHeader({ title, company, protocol, sentAt, employee, state, label, extra }) {
  const [st, cls] = STATUS[state];
  return (
    <header className="space-y-2" data-testid="dp-header">
      <h2 className="font-display text-2xl font-semibold leading-tight" data-testid="dp-title">{title}</h2>
      <div className="text-xs text-slate-500 space-y-0.5" data-testid="dp-who">
        <div>Empresa: <b className="text-slate-700">{company}</b> · Protocolo: <span className="font-mono text-indigo-700 font-semibold" data-testid={`ticket-protocol-${(protocol || "").toLowerCase()}`}>{protocol}</span></div>
        <div>Enviado em: {dateTimeBR(sentAt)}{employee ? <> · Funcionário: <b className="text-slate-700" data-testid="dp-employee">{employee}</b></> : null}</div>
      </div>
      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-bold tracking-wide ${cls}`} data-testid="dp-status">{label || st}</span>
      {extra}
    </header>);
}

export function NewAction({ onReceive }) {
  return (
    <section className="rounded-xl border-2 border-rose-200 bg-rose-50/40 p-4 space-y-3" data-testid="dp-new-box">
      <div className="text-sm font-bold text-rose-700 flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-rose-600" />NOVA DEMANDA</div>
      <Button size="lg" className="w-full h-auto min-h-11 py-3 whitespace-normal text-center leading-tight font-bold tracking-wide" onClick={onReceive} data-testid="demand-accept"><PlayCircle className="h-5 w-5 mr-2 shrink-0" />RECEBER E PROCESSAR</Button>
    </section>);
}

export function ProcessingPanel({ state, assignee, onAssignee, due, onChangeDue }) {
  const [st, cls] = STATUS[state];
  return (
    <section className="rounded-xl border bg-white p-4 grid sm:grid-cols-3 gap-4" data-testid="dp-processing">
      <div><div className="label-xs">Status</div><span className={`mt-1 inline-block rounded-full border px-2.5 py-0.5 text-xs font-bold ${cls}`}>{st}</span></div>
      <div data-testid="dp-assignee"><div className="label-xs">Responsável</div><div className="mt-1 font-semibold flex items-center gap-1.5"><UserRound className="h-4 w-4 text-slate-400" />{assignee || "—"}</div>{onAssignee}</div>
      <div data-testid="dp-promise"><div className="label-xs">Previsão de retorno</div><div className="mt-1 font-semibold flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-slate-400" />{due ? dateBR(due) : "—"}</div>
        <Button size="sm" variant="outline" className="mt-2 h-7" onClick={onChangeDue} data-testid="dp-promise-edit">Alterar prazo</Button></div>
    </section>);
}

export function ConcludeAction({ onConclude, onAsk }) {
  return (
    <div className="space-y-2">
      <Button size="lg" className="w-full h-auto min-h-11 py-3 whitespace-normal text-center leading-tight bg-emerald-600 hover:bg-emerald-700 font-bold tracking-wide" onClick={onConclude} data-testid="dp-conclude"><CheckCircle2 className="h-5 w-5 mr-2 shrink-0" />CONCLUIR E DEVOLVER AO CLIENTE</Button>
      {onAsk && <AskLink onAsk={onAsk} />}
    </div>);
}

export const AskLink = ({ onAsk }) => <button type="button" onClick={onAsk} className="w-full text-center text-sm text-slate-500 hover:text-indigo-700 inline-flex items-center justify-center gap-1.5" data-testid="dp-ask"><HelpCircle className="h-4 w-4" />Precisa de informação do cliente?</button>;
